/* eslint-disable react/no-unknown-property */
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client';
import { ToastContainer } from 'react-toastify';
import '@/styles/globals.scss';
import { ThemeProvider } from 'next-themes';
import Layout from '@/layouts/main';
import { Provider } from 'react-redux';
import type { AppProps } from 'next/app';
import { store } from '../redux/store';
import { useRouter } from 'next/router';
import { MetaMaskProvider } from 'metamask-react';
import Meta from '@/components/meta';
import UserContext from '@/components/userContext';
import { useRef } from 'react';
import typography from '@/theme/typography';
import { CookieSetting } from '@/components/cookie';
import { CookieContentModal } from '@/components/cookie/CookieContentModal';
import { ListsyncCookieSetting } from '@/components/listsync/cookie';
import { ListsyncCookieContentModal } from '@/components/listsync/cookie/CookieContentModal';
import { useExternalSource } from '@/utils/externalSource';
import FloatingContactButton from '@/components/FloatingContactButton';
import ListsyncFloatingContactButton from '@/components/listsync/FloatingContactButton';
import ListSyncLanding from '@/components/listsync-landing';
import { useEffect, useMemo, useState } from 'react';
import ListsyncTermsPage from './listsync/terms';
import ListsyncPrivacyPage from './listsync/privacy';
import { useDispatch } from 'react-redux';
import { setPreferenceCenterOpen } from '@/redux/cookieSlice';

declare global {
  interface Window {
    Intercom?: (...args: unknown[]) => void;
    intercomSettings?: Record<string, unknown>;
  }
}

const LISTSYNC_INTERCOM_APP_ID = 'ln9oqoqp';
const NESTFLO_INTERCOM_APP_ID = 'ln9oqoqp';

/** 左下角悬浮「留言」按钮（FloatingContactButton）；Intercom 不受影响 */
const SHOW_LEFT_FLOATING_CONTACT_BUTTON = false;

// 内部组件：处理 /cookies 路径自动打开弹窗
function CookieRouteHandler() {
  const dispatch = useDispatch();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setMounted(true);
    }
  }, []);

  useEffect(() => {
    if (!mounted || !router.isReady) return;

    // 使用 asPath 获取实际路径（包括查询参数）
    const currentPath = router.asPath.split('?')[0]; // 移除查询参数

    if (currentPath === '/cookies') {
      // 增加延迟确保所有组件都已挂载，包括 CookieContentModal
      const timer = setTimeout(() => {
        dispatch(setPreferenceCenterOpen(true));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [router.asPath, router.isReady, dispatch, mounted]);

  return null;
}

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const pid = router.pathname;
  const scrollRef = useRef({
    scrollPos: 0
  });
  // 处理外部来源参数并设置 cookie
  useExternalSource();

  // Domain detection for Listsync landing page
  const [isListSyncDomain, setIsListSyncDomain] = useState<boolean | null>(
    null
  );

  useEffect(() => {
    // Only run once on mount
    const hostname = window.location.hostname;
    setIsListSyncDomain(
      hostname === 'listsync.co.uk' ||
        hostname === 'www.listsync.co.uk' ||
        hostname === 'localhost' ||
        hostname === '127.0.0.1'
    );
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || isListSyncDomain === null) return;

    const appId = isListSyncDomain
      ? LISTSYNC_INTERCOM_APP_ID
      : NESTFLO_INTERCOM_APP_ID;

    const intercomSettings = {
      app_id: appId,
      theme_mode: isListSyncDomain ? 'light' : 'dark'
    };

    const bootIntercom = () => {
      window.intercomSettings = intercomSettings;
      window.Intercom?.('boot', intercomSettings);
    };

    if (window.Intercom) {
      window.Intercom('shutdown');
      bootIntercom();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    script.onload = () => {
      bootIntercom();
    };
    document.head.appendChild(script);
  }, [isListSyncDomain]);

  useEffect(() => {
    if (isListSyncDomain === null || !window.Intercom) return;
    window.Intercom('update');
  }, [router.asPath, isListSyncDomain]);

  // 使用 useMemo 缓存 ApolloClient 实例，避免每次渲染都创建新实例
  // 必须在条件返回之前调用，以遵守 React Hooks 规则
  // 根据域名动态设置 API 地址
  const client = useMemo(
    () =>
      new ApolloClient({
        uri:
          isListSyncDomain === true
            ? 'https://api.listsync.co.uk/graphql'
            : 'https://api.nestflo.ai/graphql',
        cache: new InMemoryCache()
      }),
    [isListSyncDomain]
  );

  // Show nothing while detecting domain to prevent flash
  if (isListSyncDomain === null) {
    return null;
  }

  return (
    <>
      <Meta
        title={isListSyncDomain ? 'Listsync Home' : 'Nestflo Home'}
        isListSyncDomain={isListSyncDomain}
      />
      <style jsx global>
        {`
          :root {
            --font-poppins: ${typography.fontFamily};
          }
        `}
      </style>
      {/* <Script src="https://www.googletagmanager.com/gtag/js?id=G-N52NQZK5D0" />
      <Script src="https://salesiq.zoho.com/widget" id="zsiqscript" defer />
      <Script id="google-analytics">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
 
          gtag('config', 'G-N52NQZK5D0');
        `}
      </Script>

      <Script id="zoho-desktop">{`
        var $zoho = $zoho || {};
    $zoho.salesiq = $zoho.salesiq || {
        widgetcode: "siqd690ae45c76c1b9b2a1204eebbaf642a2f4ff00b190dfbbd285f565f8b71fb6a",
        values: {},
        ready: function() {}
    };
      `}</Script>
      <Script id="gdp-cookie-notice">{`
          const handleScriptLoad = () => {
            try {
              window.gdprCookieNotice({
                locale: 'en', // This is the default value
                timeout: 500, // Time until the cookie bar appears
                expiration: 30, // This is the default value, in days
                domain: 'nestflo.ai', // If you run the same cookie notice on all subdomains, define the main domain starting with a .
                implicit: false, // Accept cookies on scroll
                statement: 'https://nestflo.ai/assets/documents/cookie-policy.pdf', // Link to your cookie statement page
                performance: ['JSESSIONID'], // Cookies in the performance category.
                analytics: ['ga'], // Cookies in the analytics category.
                marketing: ['SSID'], // Cookies in the marketing category.
              });
            } catch (error) {
              console.log(error);
            }
          };
          const script = document.createElement('script');
          script.src = '/assets/GDPRCookie/script.js';
          script.onload = handleScriptLoad;
          document.body.appendChild(script);
        `}</Script> */}
      <ApolloProvider client={client}>
        <Provider store={store}>
          <ThemeProvider enableSystem={true} attribute="class">
            <MetaMaskProvider>
              <UserContext.Provider value={{ scrollRef: scrollRef }}>
                <CookieRouteHandler />
                {isListSyncDomain ? (
                  pid === '/terms' ? (
                    <ListsyncTermsPage />
                  ) : pid === '/privacy' ? (
                    <ListsyncPrivacyPage />
                  ) : (
                    <ListSyncLanding />
                  )
                ) : ['/login', '/[code]'].includes(pid) ? (
                  <Component {...pageProps} />
                ) : (
                  <Layout>
                    <Component {...pageProps} />
                  </Layout>
                )}
                <ToastContainer
                  position="top-center"
                  theme="dark"
                  hideProgressBar
                  autoClose={3000}
                />
                {isListSyncDomain ? (
                  <>
                    <ListsyncCookieSetting />
                    <ListsyncCookieContentModal />
                    {SHOW_LEFT_FLOATING_CONTACT_BUTTON && (
                      <ListsyncFloatingContactButton />
                    )}
                  </>
                ) : (
                  <>
                    <CookieSetting />
                    <CookieContentModal />
                    {SHOW_LEFT_FLOATING_CONTACT_BUTTON && (
                      <FloatingContactButton />
                    )}
                  </>
                )}
              </UserContext.Provider>
            </MetaMaskProvider>
          </ThemeProvider>
        </Provider>
      </ApolloProvider>
      <div id="zsiqwidget"></div>
    </>
  );
}

export default MyApp;
